import { XClose } from '@/assets/icons'
import { Button } from '@/components/button/Button'
import { IconButton } from '@/components/icon-button/IconButton'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { setStripePortalDialogVisible } from '@/store/slices/ui-state.slice'
import { SubscriptionNoticeBannerVariant } from './SubscriptionNoticeBanner'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { StripeSubscriptionInterval } from '@/account/store/schemas'

interface SubscriptionBannerNoticeActionsProps {
  variant: SubscriptionNoticeBannerVariant
  onDismiss: () => void
}

/**
 * Subscription Notice Banner Actions
 *
 * Renders the action buttons shown in the subscription notice banner.
 * Includes a dismiss button and a CTA button to manage or reactivate the subscription,
 * which opens the stripe portal dialog.
 */
export default function SubscriptionNoticeBannerActions(props: SubscriptionBannerNoticeActionsProps) {
  const { variant, onDismiss } = props
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Redux state
  const stripeSubscriptionInterval = useAppSelector((state) => state.accountState.stripeSubscriptionInterval)

  /**
   * Handles the CTA button click.
   * Opens the stripe portal dialog and tracks the event.
   */
  const handleCTAClick = () => {
    dispatch(setStripePortalDialogVisible(true))
    trackEvent(AnalyticsEvent.SubscriptionNoticeBannerCTAClicked, {
      subscriptionNoticeBannerVariant: variant,
      subscriptionInterval: stripeSubscriptionInterval as StripeSubscriptionInterval,
    })
  }

  return (
    <div className="flex items-center gap-1 whitespace-nowrap">
      {/* CTA button to open stripe portal dialog */}
      <Button variant="plain" color={variant === SubscriptionNoticeBannerVariant.ACTIVE_TRIAL ? 'primary' : 'secondary'} size="sm" onClick={handleCTAClick}>
        {variant === SubscriptionNoticeBannerVariant.ACTIVE_TRIAL ? 'Manage Subscription' : 'Reactivate Plan'}
      </Button>

      <IconButton variant="plain" color="neutral" size="sm" icon={<XClose aria-hidden />} aria-label="Dismiss" onClick={onDismiss} />
    </div>
  )
}
